import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { FileText, Loader2, X, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Tip } from "@/components/Tip";

export function ChatSummarizer({ conversationId, messageCount }) {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const summarize = async () => {
    if (!conversationId || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/summarize/${conversationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(data.summary);
      setExpanded(true);
    } catch (err) {
      toast.error("Failed to generate summary");
    } finally {
      setLoading(false);
    }
  };

  if (!conversationId || messageCount < 4) return null;

  if (!summary) {
    return (
      <Tip content="Summarize this conversation">
        <button
          data-testid="summarize-btn"
          onClick={summarize}
          disabled={loading}
          className="p-2 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
          title="Summarize conversation"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" strokeWidth={1.5} />
          )}
        </button>
      </Tip>
    );
  }

  return (
    <div
      data-testid="chat-summary-panel"
      className="mx-auto max-w-3xl mb-4 bg-card/80 border border-primary/15 rounded-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/20 transition-colors"
      >
        <FileText className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-sm font-medium font-emphasis flex-1">Conversation Summary</span>
        <button
          data-testid="summary-close"
          onClick={(e) => { e.stopPropagation(); setSummary(null); }}
          className="p-1 rounded-full hover:bg-muted/50 text-muted-foreground"
        >
          <X className="w-3 h-3" />
        </button>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-4 pb-3 text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed border-t border-border/20 pt-2">
          {summary}
        </div>
      )}
    </div>
  );
}
